import React from "react";
import {
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  Input,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import CountrySelect from "../common/CountrySelect";

export const networkOrganizationFields = [
  "name",
  "contact",
  "email",
  "phone",
  "address",
  "city",
  "state",
  "country",
  "zip",
];

export const emptyNetworkOrganizationForm = networkOrganizationFields.reduce(
  (values, field) => ({ ...values, [field]: "" }),
  {}
);

export const toNetworkOrganizationFormValues = (organization = {}) =>
  networkOrganizationFields.reduce((values, field) => ({
    ...values,
    [field]: organization?.[field] || "",
  }), {});

const fieldLabels = {
  name: "School name",
  contact: "Contact person",
  email: "Email",
  phone: "Phone",
  address: "Address",
  city: "City",
  state: "State",
  country: "Country",
  zip: "Zip",
};

const NetworkOrganizationForm = ({
  form,
  errors = {},
  isSubmitting = false,
  submitLabel,
  onCancel,
  onChange,
  onSubmit,
}) => {
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const sectionBg = useColorModeValue("gray.50", "gray.700");

  return (
    <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
      <CardBody>
        <form onSubmit={onSubmit}>
          <VStack align="stretch" spacing={4}>
            <Box bg={sectionBg} p={4} borderRadius="md">
              <Heading as="h4" size="sm" mb={3}>General Information</Heading>
              <FormControl isRequired isInvalid={Boolean(errors.name)}>
                <FormLabel>{fieldLabels.name}</FormLabel>
                <Input name="name" value={form.name} onChange={onChange} />
                <FormErrorMessage>{errors.name}</FormErrorMessage>
              </FormControl>
              <FormControl mt={3}>
                <FormLabel>{fieldLabels.contact}</FormLabel>
                <Input name="contact" value={form.contact} onChange={onChange} />
              </FormControl>
            </Box>

            <Box bg={sectionBg} p={4} borderRadius="md">
              <Heading as="h4" size="sm" mb={3}>Contact Information</Heading>
              <FormControl isRequired isInvalid={Boolean(errors.email)}>
                <FormLabel>{fieldLabels.email}</FormLabel>
                <Input name="email" type="email" value={form.email} onChange={onChange} />
                <FormErrorMessage>{errors.email}</FormErrorMessage>
              </FormControl>
              <FormControl mt={3}>
                <FormLabel>{fieldLabels.phone}</FormLabel>
                <Input name="phone" value={form.phone} onChange={onChange} />
              </FormControl>
            </Box>

            <Box bg={sectionBg} p={4} borderRadius="md">
              <Heading as="h4" size="sm" mb={3}>Location</Heading>
              <FormControl>
                <FormLabel>{fieldLabels.address}</FormLabel>
                <Input name="address" value={form.address} onChange={onChange} />
              </FormControl>
              <FormControl mt={3}>
                <FormLabel>{fieldLabels.city}</FormLabel>
                <Input name="city" value={form.city} onChange={onChange} />
              </FormControl>
              <FormControl mt={3}>
                <FormLabel>{fieldLabels.state}</FormLabel>
                <Input name="state" value={form.state} onChange={onChange} />
              </FormControl>
              <FormControl mt={3}>
                <FormLabel>{fieldLabels.country}</FormLabel>
                <CountrySelect
                  name="country"
                  value={form.country}
                  onChange={onChange}
                  placeholder="Select country"
                />
              </FormControl>
              <FormControl mt={3}>
                <FormLabel>{fieldLabels.zip}</FormLabel>
                <Input name="zip" value={form.zip} onChange={onChange} />
              </FormControl>
            </Box>

            <HStack justify="flex-end" flexWrap="wrap">
              <Button variant="ghost" onClick={onCancel}>
                Cancel
              </Button>
              <Button type="submit" colorScheme="blue" variant="outline" isLoading={isSubmitting}>
                {submitLabel}
              </Button>
            </HStack>
          </VStack>
        </form>
      </CardBody>
    </Card>
  );
};

export default NetworkOrganizationForm;
